import { Box, Drawer, IconButton, Paper, Tooltip, Typography } from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { BasePathProvider, useSession } from 'components/Providers'
import { search } from 'store/settings/marking-period'
import { EditIcon } from 'components/Icons'
import NoRecords from 'components/NoRecords'
import dayjs from 'dayjs'
import MarkingPeriodForm from './Form'
import { hasFullPermission } from 'store'

const baseUrl = '/settings/marking-periods'

export default function MarkingPeriodsPage() {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { academicYear } = useSession()
  const { id } = useParams()
  const data = useSelector(state => state.settings.markingPeriod.list)

  useEffect(() => {
    if (academicYear?.id) dispatch(search({ academicYearId: academicYear.id }))
  }, [academicYear?.id])

  const columns = [
    { field: 'name', headerName: t('Name'), flex: 1 },
    {
      field: 'startDate',
      headerName: t('Start Date'),
      flex: 1,
      renderCell: ({ row }) => dayjs(row.startDate).format('MM/DD/YYYY'),
    },
    {
      field: 'endDate',
      headerName: t('End Date'),
      flex: 1,
      renderCell: ({ row }) => dayjs(row.endDate).format('MM/DD/YYYY'),
    },
    {
      field: 'readOnly',
      headerName: t('Read Only'),
      flex: 1,
      renderCell: ({ row }) => (row.readOnly ? t('Yes') : t('No')),
    },
    hasFullPermission('setting.markingperiod') && {
      field: 'actions',
      headerName: t('Actions'),
      width: 100,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({ row }) => (
        <Tooltip title={t('Edit')}>
          <IconButton size="small" onClick={() => navigate(`${baseUrl}/${row.id}`)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },

  ]

  return (
    <BasePathProvider value={baseUrl}>
      <Box sx={{ pt: 2 }}>
        {data && (
          <Paper elevation={0} sx={{ overflow: 'hidden', p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="subtitle1" fontWeight={600}>{academicYear.name} Academic Year</Typography>
              </Box>
                <Typography variant="body2" color="text.secondary">
                  {dayjs(academicYear.startDate).format('MM/DD/YYYY')} – {dayjs(academicYear.endDate).format('MM/DD/YYYY')}
                </Typography>
            </Box>
            <DataGrid
              rows={data ?? []}
              columns={columns}
              getRowId={row => row.id}
              autoHeight
              hideFooter
              disableColumnMenu
              loading={!data}
              slots={{ noRowsOverlay: NoRecords }}
            />
          </Paper>
        )}

        <Drawer
          PaperProps={{ sx: { width: 480 } }}
          open={Boolean(id)}
          anchor="right"
          onClose={() => navigate(baseUrl)}
        >
          <MarkingPeriodForm />
        </Drawer>
      </Box>
    </BasePathProvider>
  )
}
